import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { clockIn, clockOut, getAttendanceStatus } from "../../../services/attendance";
import moment from "moment";

export default function ClockInOut() {
    const [status, setStatus] = useState(null);
    const [isClockedIn, setIsClockedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const [kmFromImage, setKmFromImage] = useState(null);
    const [kmToImage, setKmToImage] = useState(null);
    const [kmFrom, setKmFrom] = useState("");
    const [kmTo, setKmTo] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Vehicle KM images are optional for every role now — a clock-out is
    // accepted with or without them (the backend allows this too). Super Admin
    // (1) does not clock in at all.

    useEffect(() => {
        loadAttendanceStatus();
    }, []);

    const loadAttendanceStatus = async () => {
        setLoading(true);
        try {
            const res = await getAttendanceStatus();
            if (res && res.status) {
                setStatus(res.data.attendance);
                setIsClockedIn(res.data.is_clocked_in);
            }
        } catch (error) {
            toast.error("Error loading attendance status");
        }
        setLoading(false);
    };

    const handleClockIn = async () => {
        setSubmitting(true);
        try {
            const res = await clockIn();
            if (res && res.status) {
                loadAttendanceStatus();
            }
        } catch (error) {
            // Error already handled in service
        }
        setSubmitting(false);
    };

    const handleClockOut = async () => {
        // Vehicle KM images are optional for everyone — clock out with or without.
        setSubmitting(true);
        try {
            const formData = new FormData();
            if (kmFromImage) formData.append("vehicle_km_from_image", kmFromImage);
            if (kmToImage) formData.append("vehicle_km_to_image", kmToImage);
            formData.append("user_id", localStorage.getItem("UID"));
            if (kmFrom) formData.append("vehicle_km_from", kmFrom);
            if (kmTo) formData.append("vehicle_km_to", kmTo);

            const res = await clockOut(formData);
            if (res && res.status) {
                setKmFromImage(null);
                setKmToImage(null);
                setKmFrom("");
                setKmTo("");
                loadAttendanceStatus();
            }
        } catch (error) {
            // Error already handled in service
        }
        setSubmitting(false);
    };

    if (loading) {
        return (
            <div className="app-content main-content">
                <div className="side-app main-container">
                    <div className="page-header d-xl-flex d-block">
                        <div className="page-leftheader">
                            <div className="page-title">Clock In / Clock Out</div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-xl-12 col-lg-12 col-md-12 col-xm-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="text-center">
                                        <div className="spinner-border" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Super Admin (1) and Admin (2) do not clock in/out; the API rejects it too.
    if (["1", "2"].includes(localStorage.getItem("Role"))) {
        return (
            <div className="app-content main-content">
                <div className="side-app main-container">
                    <div className="alert alert-info mt-4">
                        Clock in/out is not required for your role.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="app-content main-content">
            <div className="side-app main-container">
                <div className="page-header d-xl-flex d-block">
                    <div className="page-leftheader">
                        <div className="page-title">Clock In / Clock Out</div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xl-8 col-lg-10 col-md-12 col-xm-12">
                        <div className="card">
                            <div className="card-header">
                                <h4 className="card-title">Attendance</h4>
                            </div>
                            <div className="card-body">
                                {status && (
                                    <div className="alert alert-info">
                                        <strong>Today's Status:</strong>
                                        <br />
                                        Clock In: {moment(status.date).format("DD/MM/YYYY")} {status.clock_in_time}
                                        {status.clock_out && (
                                            <>
                                                <br />
                                                Clock Out: {moment(status.date).format("DD/MM/YYYY")} {status.clock_out_time}
                                                <br />
                                                Working Hours: {status.working_hours} hours
                                            </>
                                        )}
                                    </div>
                                )}

                                {!isClockedIn && !status && (
                                    <div className="text-center mb-4">
                                        <button
                                            className="btn btn-primary btn-lg"
                                            onClick={handleClockIn}
                                            disabled={submitting}
                                        >
                                            {submitting ? "Processing..." : "Clock In"}
                                        </button>
                                    </div>
                                )}

                                {isClockedIn && (
                                    <div>
                                        <h5 className="mb-3">
                                            Clock Out - Vehicle KM Images (Optional)
                                        </h5>

                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Vehicle KM From Image (Optional)</label>
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={(e) => setKmFromImage(e.target.files[0])}
                                                    />
                                                    {kmFromImage && (
                                                        <small className="text-success">
                                                            Selected: {kmFromImage.name}
                                                        </small>
                                                    )}
                                                </div>
                                                <div className="form-group">
                                                    <label>Vehicle KM From (Optional)</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        placeholder="Enter starting KM"
                                                        value={kmFrom}
                                                        onChange={(e) => setKmFrom(e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Vehicle KM To Image (Optional)</label>
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={(e) => setKmToImage(e.target.files[0])}
                                                    />
                                                    {kmToImage && (
                                                        <small className="text-success">
                                                            Selected: {kmToImage.name}
                                                        </small>
                                                    )}
                                                </div>
                                                <div className="form-group">
                                                    <label>Vehicle KM To (Optional)</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        placeholder="Enter ending KM"
                                                        value={kmTo}
                                                        onChange={(e) => setKmTo(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="text-center mt-3">
                                            <button
                                                className="btn btn-danger btn-lg"
                                                onClick={handleClockOut}
                                                disabled={submitting}
                                            >
                                                {submitting ? "Processing..." : "Clock Out"}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {!isClockedIn && status && (
                                    <div className="alert alert-success text-center">
                                        <h5>You have already clocked out for today!</h5>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
